import React, { useCallback, useMemo, useRef } from "react";
import styles from "./style.scss";
import { Streamdown } from "streamdown";
import { ReferenceTooltip } from "./ReferenceToolTip";
import { Reference as ReferenceType } from "models/AiResearch";
import { formatDate } from "utils/helper/day";

interface Props {
  text: string;
  onClickReference?: (id: number) => void;
  selectedReference?: ReferenceType | null;
  references?: ReferenceType[];
}

const skipElementTypes = new Set(["code", "pre", "a"]);

const buildReferenceTooltipContent = (title: string, meta?: string) => (
  <span className={styles.content}>
    {meta ? <span className={styles.meta}>{meta}</span> : null}
    <span className={styles.title}>{title}</span>
  </span>
);

// citationNumber から対応する reference を引き、tooltip に出す文言へ変換する。
const getReferenceTooltipContent = (
  referenceId: number,
  selectedReference?: ReferenceType | null,
  references?: ReferenceType[]
): React.ReactNode => {
  const reference =
    selectedReference?.citationNumber === referenceId
      ? selectedReference
      : references?.find(item => item.citationNumber === referenceId);

  if (!reference) return null;

  switch (reference.dataType) {
    case "article_chunk": {
      const date = reference.data.publishDate
        ? formatDate(String(reference.data.publishDate), "YYYY年MM月DD日")
        : "";
      const meta = [date, reference.data.mediaName].filter(Boolean).join("　");
      return buildReferenceTooltipContent(reference.data.title, meta);
    }
    case "financial_chart":
    case "macro_chart":
    case "macro_ir_chart": {
      const meta = [
        reference.data.meta.companyName,
        reference.data.meta.industryName
      ]
        .filter(Boolean)
        .join(" / ");
      return buildReferenceTooltipContent(reference.data.meta.name, meta);
    }
    case "financial_table": {
      const title = reference.data.content.name || "";
      return buildReferenceTooltipContent(title);
    }
    case "company_list_table": {
      const title = reference.data.content.name || "";
      return buildReferenceTooltipContent(title);
    }
    default:
      return null;
  }
};

type RendererProps = {
  children?: React.ReactNode;
  node?: unknown;
  [key: string]: unknown;
};

const referenceTags = [
  "p",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "strong",
  "em",
  "del",
  "blockquote",
  "ul",
  "ol",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "span",
  "div"
] as const;

const renderTable: React.FC<RendererProps & { className?: string }> = ({
  children,
  node: _,
  className,
  ...props
}) => (
  <div className={styles.tableWrapper}>
    <table
      {...props}
      className={className ? `${className} ${styles.vsTable}` : styles.vsTable}
    >
      {children}
    </table>
  </div>
);

const renderReferenceButtonsInString = (
  value: string,
  onClickReference: (id: number) => void,
  getTooltipContent: (id: number) => React.ReactNode
): React.ReactNode => {
  // "[1]" のような参照表記だけを button + tooltip に置き換える。
  return value.split(/(\[\d+\])/g).map((part, index) => {
    const match = part.match(/^\[(\d+)\]$/);
    if (!match) return part;

    const referenceId = Number(match[1]);
    return (
      <ReferenceTooltip
        key={`ref-${index}-${referenceId}`}
        content={() => getTooltipContent(referenceId)}
      >
        <button
          className={styles.referenceButton}
          aria-label={`Reference ${referenceId}`}
          type="button"
          onClick={() => onClickReference(referenceId)}
        >
          {referenceId}
        </button>
      </ReferenceTooltip>
    );
  });
};

const replaceReferences = (
  node: React.ReactNode,
  onClickReference: (id: number) => void,
  getTooltipContent: (id: number) => React.ReactNode
): React.ReactNode => {
  // 文字列ノードから順に辿って、Markdown の描画結果へ参照 button を差し込む。
  if (typeof node === "string") {
    return renderReferenceButtonsInString(
      node,
      onClickReference,
      getTooltipContent
    );
  }

  if (Array.isArray(node)) {
    return node.map(item =>
      replaceReferences(item, onClickReference, getTooltipContent)
    );
  }

  if (React.isValidElement(node)) {
    if (typeof node.type === "string" && skipElementTypes.has(node.type)) {
      return node;
    }

    const currentChildren = (node.props as { children?: React.ReactNode })
      .children;
    const nextChildren = replaceReferences(
      currentChildren,
      onClickReference,
      getTooltipContent
    );
    return React.cloneElement(node, undefined, nextChildren);
  }

  return node;
};

export const AiResearchText: React.FC<Props> = ({
  text,
  onClickReference,
  selectedReference,
  references
}) => {
  // Streamdown に渡す renderer の参照は固定しつつ、内部では常に最新 props を読む。
  const latestPropsRef = useRef({
    onClickReference,
    selectedReference,
    references
  });

  latestPropsRef.current = {
    onClickReference,
    selectedReference,
    references
  };

  const handleReferenceClick = useCallback((referenceId: number) => {
    latestPropsRef.current.onClickReference?.(referenceId);
  }, []);

  const getTooltipContent = useCallback((referenceId: number) => {
    const currentProps = latestPropsRef.current;
    return getReferenceTooltipContent(
      referenceId,
      currentProps.selectedReference,
      currentProps.references
    );
  }, []);

  const components = useMemo(() => {
    // Streamdown の各タグ renderer から共通の参照置換処理を呼ぶ。
    const nextComponents = referenceTags.reduce((acc, tag) => {
      acc[tag] = ({ children, node: _, ...props }: RendererProps) => {
        // renderer 自体を再生成しないことで、reference button 配下の state を維持する。
        return React.createElement(
          tag,
          props,
          replaceReferences(children, handleReferenceClick, getTooltipContent)
        );
      };
      return acc;
    }, {} as Record<string, React.ComponentType<RendererProps>>);

    nextComponents.table = renderTable;
    nextComponents.hr = () => null;
    // Mermaid記法のコードブロックはStreamdownの処理対象外とする
    nextComponents.code = ({
      className,
      children
    }: RendererProps & { className?: string }) => {
      if (
        typeof className === "string" &&
        className.includes("language-mermaid")
      ) {
        return (
          <pre>
            <code>{children}</code>
          </pre>
        );
      }
      return <code className={className}>{children}</code>;
    };

    return nextComponents;
  }, [getTooltipContent, handleReferenceClick]);

  return (
    <div className={styles.container}>
      <Streamdown
        className={styles.streamdown}
        components={components}
        linkSafety={{ enabled: false }}
      >
        {text}
      </Streamdown>
    </div>
  );
};
